import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bell, BellOff, Play, X } from "lucide-react";
import api from "@/api";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";

export const RINGTONES = [
  { id: "campanella", label: "🔔 Campanella" },
  { id: "sirena", label: "🚨 Sirena" },
  { id: "tripla", label: "📢 Tripla nota" },
  { id: "dingdong", label: "🛎 Ding-dong" },
  { id: "allarme", label: "⏰ Allarme industriale" },
  { id: "trillo", label: "🎵 Trillo" },
];
const LS_KEY = "cc_sound_alerts";
const DEFAULTS = { enabled: true, general: "campanella", pastry: "dingdong", bakery: "tripla", messages: "trillo", panettone: "campanella" };
export const soundCfg = () => { try { return { ...DEFAULTS, ...(JSON.parse(localStorage.getItem(LS_KEY)) || {}) }; } catch { return { ...DEFAULTS }; } };
const saveCfg = (c) => localStorage.setItem(LS_KEY, JSON.stringify(c));
export const playRingtone = (id) => { try { const a = new Audio(`/sounds/${id}.wav`); a.volume = 1; a.play().catch(() => {}); } catch { /* autoplay bloccato */ } };

const deptKind = (depts, depId) => {
  const d = (depts || []).find((x) => x.id === depId);
  const s = `${depId || ""} ${(d && d.name) || ""}`.toLowerCase();
  if (/panetton/.test(s)) return "panettone";
  if (/pasticc|pastry|dolc/.test(s)) return "pastry";
  if (/panific|bakery|forno|pane/.test(s)) return "bakery";
  return "general";
};

// Suoneria effettiva per un reparto: dedicata (dept_{id}) → legacy per tipo → generale.
// Così anche i reparti/cataloghi creati dopo hanno la propria suoneria.
export const ringForDept = (cfg, depts, depId) => {
  const own = cfg[`dept_${depId || "bakery"}`];
  if (own) return own;
  const k = deptKind(depts, depId);
  return cfg[k] || cfg.general;
};

// Avvisi sonori nuovi ordini: suoneria per tipo, disattivabile SOLO su questo dispositivo (localStorage)
export default function OrderSoundAlerts() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [cfg, setCfg] = useState(soundCfg());
  const [open, setOpen] = useState(false);
  const since = useRef(null);

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    let es = null;
    let pollIv = null;
    let watchIv = null;
    let lastSeen = Date.now();
    const handle = (payload) => {
      if (payload && payload.hb) return; // heartbeat: canale vivo
      const c = soundCfg();
      const hasNews = (payload.new || []).length > 0 || payload.new_messages > 0;
      if (hasNews) window.dispatchEvent(new CustomEvent("cc-orders-changed", { detail: payload }));
      if (!c.enabled) return;
      const rings = [...new Set((payload.new || []).map((o) => ringForDept(c, user.departments, o.department)))];
      if (payload.new_messages > 0) rings.push(c.messages || c.general);
      rings.forEach((ring, i) => {
        if (ring && ring !== "off") setTimeout(() => playRingtone(ring), i * 1800);
      });
    };
    const startPollingFallback = () => {
      if (pollIv) return;
      const poll = async () => {
        try {
          const r = await api.ordersNewSince(since.current);
          const first = !since.current;
          since.current = r.latest;
          if (!first) handle(r);
        } catch { /* offline */ }
      };
      poll();
      pollIv = setInterval(poll, 15000);
    };
    try {
      // tempo reale: come un messaggio sul telefono
      const base = process.env.REACT_APP_BACKEND_URL || "";
      es = new EventSource(`${base}/api/orders/stream?token=${encodeURIComponent(token)}`);
      es.onmessage = (ev) => { lastSeen = Date.now(); try { handle(JSON.parse(ev.data)); } catch { /* ignore */ } };
      es.onerror = () => { /* EventSource riprova da solo; se chiuso definitivamente, fallback */
        if (es && es.readyState === 2) { startPollingFallback(); }
      };
      // watchdog: se il canale resta muto (proxy/ingress che blocca SSE) passa al polling
      watchIv = setInterval(() => {
        if (Date.now() - lastSeen > 70000) {
          try { es && es.close(); } catch { /* già chiuso */ }
          startPollingFallback();
        }
      }, 25000);
    } catch {
      startPollingFallback();
    }
    return () => { if (es) es.close(); if (pollIv) clearInterval(pollIv); if (watchIv) clearInterval(watchIv); };
  }, [user]); // eslint-disable-line

  const set = (k, v) => { const c = { ...cfg, [k]: v }; setCfg(c); saveCfg(c); };
  if (!user) return null;

  const row = (key, label, fallback) => {
    const val = cfg[key] || fallback || cfg.general;
    return (
      <div className="flex items-center gap-2 py-2 border-b border-border/40" data-testid={`sound-row-${key}`}>
        <span className="flex-1 text-sm font-semibold">{label}</span>
        <select className="field py-1 text-sm w-44" value={val} onChange={(e) => set(key, e.target.value)} data-testid={`sound-select-${key}`}>
          {RINGTONES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          <option value="off">🔇 {t("soundOff")}</option>
        </select>
        <button onClick={() => val !== "off" && playRingtone(val)} className="p-1.5 rounded-lg hover:bg-muted text-accent" title={t("soundTest")} data-testid={`sound-test-${key}`}>
          <Play className="w-4 h-4" />
        </button>
      </div>
    );
  };

  return (
    <>
      <button onClick={() => setOpen(true)}
        className={`fixed bottom-4 left-4 z-40 rounded-full shadow-lg border border-border p-2.5 ${cfg.enabled ? "bg-white text-accent" : "bg-muted text-muted-foreground"}`}
        title={t("soundBtnTip")} data-testid="sound-alerts-btn">
        {cfg.enabled ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="sound-alerts-modal">
          <div className="bg-card rounded-2xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-bold text-lg flex items-center gap-2">🔊 {t("soundTitle")}</h3>
              <button onClick={() => setOpen(false)} className="p-1.5 text-muted-foreground hover:text-foreground" data-testid="sound-alerts-close"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-muted-foreground mb-3">{t("soundHint")}</p>
            <button onClick={() => set("enabled", !cfg.enabled)}
              className={`w-full rounded-xl py-2.5 font-bold text-sm mb-3 ${cfg.enabled ? "bg-emerald-100 text-emerald-700" : "bg-destructive/10 text-destructive"}`}
              data-testid="sound-enabled-toggle">
              {cfg.enabled ? `🔔 ${t("soundOnDevice")}` : `🔇 ${t("soundOffDevice")}`}
            </button>
            {(user.departments || []).map((d) => {
              const k = deptKind(user.departments, d.id);
              return row(`dept_${d.id}`, `📦 ${d.name}`, cfg[k] || DEFAULTS[k]);
            })}
            {row("general", t("soundRowDeptFallback"))}
            {row("messages", t("soundRowMessages"))}
            <p className="text-[11px] text-muted-foreground mt-3">{t("soundDeviceNote")}</p>
          </div>
        </div>, document.body)}
    </>
  );
}
